import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Mail, Lock, User, ArrowRight, Headphones, Shield, AtSign, Loader2, Check, X, Phone } from 'lucide-react';
import { API } from '../../config/api';
import { checkHandleAvailability as checkHandle } from '../../utils/safeJson';



export default function ListenerSignupPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [checkingHandle, setCheckingHandle] = useState(false);
  const [handleAvailable, setHandleAvailable] = useState(null);
  const [handleError, setHandleError] = useState('');
  
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    handle: '',
    phone: '',
    agreedToGuidelines: false
  });
  const abortControllerRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  const validateHandle = (handle) => {
    if (handle.length < 3) return 'Handle must be at least 3 characters';
    if (handle.length > 20) return 'Handle must be 20 characters or less';
    if (!/^[a-z0-9_]+$/.test(handle)) return 'Only lowercase letters, numbers, and underscores allowed';
    return '';
  };

  const checkHandleAvailability = async (handle) => {
    // Cancel previous request
    if (abortControllerRef.current) abortControllerRef.current.abort();
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    if (!handle || handle.length < 3) {
      setHandleAvailable(null);
      return;
    }
    
    if (!/^[a-z0-9_]+$/.test(handle)) {
      setHandleError('Only lowercase letters, numbers, and underscores');
      setHandleAvailable(false);
      return;
    }
    
    debounceTimerRef.current = setTimeout(async () => {
      abortControllerRef.current = new AbortController();
      setCheckingHandle(true);
      setHandleError('');
      
      try {
        const result = await checkHandle(handle, API, abortControllerRef.current.signal);
        if (result.error !== 'aborted') {
          setHandleAvailable(result.available);
          if (!result.available) setHandleError(result.reason || 'Handle not available');
        }
      } catch (err) {
        setHandleError('Could not check availability');
      } finally {
        setCheckingHandle(false);
      }
    }, 400);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.agreedToGuidelines) {
      toast.error('You must agree to the Community Guidelines');
      return;
    }
    
    if (!handleAvailable) {
      toast.error('Please choose an available @handle');
      return;
    }
    
    setLoading(true);
    try {
      // Register with role and handle
      const res = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: form.email,
          password: form.password,
          name: form.name,
          role: 'listener',
          handle: form.handle,
          phone_number: form.phone || null,
          agreed_to_guidelines: form.agreedToGuidelines
        })
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail?.message || error.detail || 'Registration failed');
      }
      
      const data = await res.json();
      localStorage.setItem('token', data.access_token);
      
      toast.success('Welcome to DMV Soundstage!');
      navigate('/feed');
    } catch (error) {
      toast.error(error.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    const redirectUrl = window.location.origin + '/auth/callback?role=listener';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" data-testid="listener-signup-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-gray-500/20 flex items-center justify-center mx-auto mb-4">
            <Headphones className="w-8 h-8 text-gray-400" />
          </div>
          <h1 className="font-heading text-3xl font-bold uppercase mb-2">Join as Listener</h1>
          <p className="text-muted-foreground">Discover DMV artists, stream music, and support the scene</p>
        </div>

        <Card className="glass-card border-border p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="name"
                  type="text"
                  placeholder="Your name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="pl-10"
                  required
                  data-testid="listener-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="handle">@Handle (required)</Label>
              <div className="relative">
                <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="handle"
                  type="text"
                  placeholder="yourhandle"
                  value={form.handle}
                  onChange={(e) => {
                    const handle = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
                    setForm({ ...form, handle });
                    checkHandleAvailability(handle);
                  }}
                  className={`pl-10 pr-10 ${handleAvailable === true ? 'border-green-500' : handleAvailable === false ? 'border-red-500' : ''}`}
                  required
                  data-testid="listener-handle-input"
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {checkingHandle && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                  {!checkingHandle && handleAvailable === true && <Check className="w-4 h-4 text-green-500" />}
                  {!checkingHandle && handleAvailable === false && <X className="w-4 h-4 text-red-500" />}
                </div>
              </div>
              {handleError && <p className="text-xs text-red-400">{handleError}</p>}
              {handleAvailable && <p className="text-xs text-green-400">@{form.handle} is available!</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="pl-10"
                  required
                  data-testid="listener-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number <span className="text-muted-foreground text-xs">(Optional)</span></Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="phone"
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/[^\d]/g, '').slice(0, 10) })}
                  className="pl-10"
                  data-testid="listener-phone-input"
                />
              </div>
              <p className="text-xs text-muted-foreground">For account recovery and notifications</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="pl-10"
                  required
                  minLength={6}
                  data-testid="listener-password-input"
                />
              </div>
            </div>

            <div className="p-4 rounded-lg bg-muted/30 border border-border space-y-3">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="guidelines"
                  checked={form.agreedToGuidelines}
                  onCheckedChange={(checked) => setForm({ ...form, agreedToGuidelines: checked })}
                  data-testid="listener-guidelines-checkbox"
                />
                <Label htmlFor="guidelines" className="text-sm leading-relaxed cursor-pointer">
                  I agree to the{' '}
                  <Link to="/community-guidelines" target="_blank" className="text-secondary hover:underline">
                    Community Guidelines
                  </Link>
                </Label>
              </div>
              <p className="text-xs text-muted-foreground flex items-center gap-1.5 ml-7">
                <Shield className="w-3 h-3" />
                Required to create an account
              </p>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading || !handleAvailable}
              data-testid="listener-signup-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
              Create Listener Account
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">Or</span>
            </div>
          </div>

          <Button
            variant="outline"
            className="w-full border-border hover:bg-muted/50"
            onClick={handleGoogleSignup}
            data-testid="listener-google-btn"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-secondary hover:underline">Sign in</Link>
          </p>

          <p className="text-center text-xs text-muted-foreground mt-4">
            Are you an artist or creative?{' '}
            <Link to="/signup/artist" className="text-secondary hover:underline">Sign up as Artist</Link>
          </p>
        </Card>
      </div>
    </div>
  );
}
